import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, TextField, Select, MenuItem, InputLabel, FormControl, Grid, Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Pagination, IconButton, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import api from '../../api';
import Spinner from '../Spinner';

const sectionTypes = [
  { value: 'dictionary', label: 'Dictionary' },
  { value: 'idiom', label: 'Idioms' },
  { value: 'grammar', label: 'Grammar Lab' },
  { value: 'writing', label: 'Writing Studio' },
  { value: 'reading', label: 'Reading Lounge' },
  { value: 'etymology', label: 'Etymology Index' },
  { value: 'syllabification', label: 'Syllabification' },
  { value: 'daily-challenge', label: 'Daily Challenge' }
];

const AdminLanguageHubTab: React.FC = () => {
  const [selectedType, setSelectedType] = useState('dictionary');
  const [items, setItems] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Dialog / Edit states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  
  // Dynamic Form Buffers
  const [word, setWord] = useState('');
  const [partOfSpeech, setPartOfSpeech] = useState('');
  const [synonymsText, setSynonymsText] = useState('');
  const [antonymsText, setAntonymsText] = useState('');
  const [definition, setDefinition] = useState('');
  const [example, setExample] = useState('');
  const [idiom, setIdiom] = useState('');
  const [meaning, setMeaning] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [passage, setPassage] = useState('');
  const [origin, setOrigin] = useState('');
  const [history, setHistory] = useState('');
  const [parts, setParts] = useState(''); // comma separated
  const [beats, setBeats] = useState<number>(1);
  const [date, setDate] = useState(''); // for daily challenge
  
  // Custom Quiz Fields (for Reading Lounge / Daily Challenge)
  const [quizQuestion, setQuizQuestion] = useState('');
  const [quizOptionA, setQuizOptionA] = useState('');
  const [quizOptionB, setQuizOptionB] = useState('');
  const [quizOptionC, setQuizOptionC] = useState('');
  const [quizOptionD, setQuizOptionD] = useState('');
  const [quizCorrect, setQuizCorrect] = useState('A');
  const [quizExplanation, setQuizExplanation] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let queryStr = `?type=${selectedType}&page=${page}&limit=${limit}`;
      if (search.trim()) {
        queryStr += `&search=${encodeURIComponent(search.trim())}`;
      }
      const res = await api.get(`/language-hub${queryStr}`);
      if (res.data && res.data.success) {
        setItems(res.data.data);
        setTotalPages(res.data.pagination.pages || 1);
      } else {
        setError('Failed to fetch Language Hub items.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error fetching items');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedType]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchItems();
  };

  const openAddDialog = () => {
    setEditingItem(null);
    // Clear forms
    setWord(''); setPartOfSpeech(''); setSynonymsText(''); setAntonymsText(''); setDefinition(''); setExample(''); setIdiom(''); setMeaning('');
    setTitle(''); setCategory(''); setContent(''); setPassage(''); setOrigin('');
    setHistory(''); setParts(''); setBeats(1); setDate(new Date().toISOString().split('T')[0]);
    setQuizQuestion(''); setQuizOptionA(''); setQuizOptionB(''); setQuizOptionC(''); setQuizOptionD('');
    setQuizCorrect('A'); setQuizExplanation('');
    setDialogOpen(true);
  };

  const openEditDialog = (item: any) => {
    setEditingItem(item);
    // Populate form buffers
    setWord(item.word || '');
    setPartOfSpeech(item.partOfSpeech || '');
    setSynonymsText(Array.isArray(item.synonyms) ? item.synonyms.join('; ') : '');
    setAntonymsText(Array.isArray(item.antonyms) ? item.antonyms.join('; ') : '');
    setDefinition(item.definition || '');
    setExample(item.examples && item.examples.length > 0 ? item.examples[0] : '');
    setIdiom(item.idiom || '');
    setMeaning(item.meaning || '');
    setTitle(item.title || '');
    setCategory(item.category || '');
    setContent(item.content || '');
    setPassage(item.passage || '');
    setOrigin(item.origin || '');
    setHistory(item.history || '');
    setParts(item.parts ? item.parts.join(', ') : '');
    setBeats(item.beats || 1);
    setDate(item.date ? new Date(item.date).toISOString().split('T')[0] : '');

    // Populate quiz buffer if present
    if (item.quizzes && item.quizzes.length > 0) {
      const q = item.quizzes[0];
      setQuizQuestion(q.question || '');
      setQuizOptionA(q.options && q.options.length > 0 ? q.options[0] : '');
      setQuizOptionB(q.options && q.options.length > 1 ? q.options[1] : '');
      setQuizOptionC(q.options && q.options.length > 2 ? q.options[2] : '');
      setQuizOptionD(q.options && q.options.length > 3 ? q.options[3] : '');
      setQuizCorrect(q.correctAnswer ? (q.options.indexOf(q.correctAnswer) === 1 ? 'B' : q.options.indexOf(q.correctAnswer) === 2 ? 'C' : q.options.indexOf(q.correctAnswer) === 3 ? 'D' : 'A') : 'A');
      setQuizExplanation(q.explanation || '');
    } else {
      setQuizQuestion(''); setQuizOptionA(''); setQuizOptionB(''); setQuizOptionC(''); setQuizOptionD('');
      setQuizCorrect('A'); setQuizExplanation('');
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    try {
      const payload: any = { type: selectedType };

      // Build payload based on selected type
      if (selectedType === 'dictionary') {
        payload.word = word;
        payload.partOfSpeech = partOfSpeech;
        payload.synonyms = synonymsText.split(/[;,]/).map(value => value.trim()).filter(Boolean);
        payload.antonyms = antonymsText.split(/[;,]/).map(value => value.trim()).filter(Boolean);
        payload.definition = definition;
        payload.examples = example.trim() ? [example] : [];
      } else if (selectedType === 'idiom') {
        payload.idiom = idiom;
        payload.meaning = meaning;
        payload.examples = example.trim() ? [example] : [];
      } else if (selectedType === 'grammar') {
        payload.title = title;
        payload.category = category;
        payload.content = content;
        payload.examples = example.trim() ? example.split('\n').filter(Boolean) : [];
      } else if (selectedType === 'writing') {
        payload.title = title;
        payload.category = category;
        payload.content = content;
      } else if (selectedType === 'reading') {
        payload.title = title;
        payload.category = category;
        payload.passage = passage;
        if (quizQuestion.trim()) {
          const opts = [quizOptionA, quizOptionB, quizOptionC, quizOptionD].filter(Boolean);
          const correctVal = quizCorrect === 'B' ? quizOptionB : quizCorrect === 'C' ? quizOptionC : quizCorrect === 'D' ? quizOptionD : quizOptionA;
          payload.quizzes = [{
            question: quizQuestion,
            options: opts,
            correctAnswer: correctVal,
            explanation: quizExplanation
          }];
        }
      } else if (selectedType === 'etymology') {
        payload.word = word;
        payload.origin = origin;
        payload.definition = definition;
        payload.history = history;
      } else if (selectedType === 'syllabification') {
        payload.word = word;
        payload.parts = parts.split(',').map(s => s.trim()).filter(Boolean);
        payload.beats = beats;
        payload.definition = definition;
      } else if (selectedType === 'daily-challenge') {
        payload.word = word;
        payload.definition = definition;
        payload.examples = example.trim() ? [example] : [];
        payload.date = date ? new Date(date) : new Date();
        if (quizQuestion.trim()) {
          const opts = [quizOptionA, quizOptionB, quizOptionC, quizOptionD].filter(Boolean);
          const correctVal = quizCorrect === 'B' ? quizOptionB : quizCorrect === 'C' ? quizOptionC : quizCorrect === 'D' ? quizOptionD : quizOptionA;
          payload.quizzes = [{
            question: quizQuestion,
            options: opts,
            correctAnswer: correctVal,
            explanation: quizExplanation
          }];
        }
      }

      if (editingItem) {
        // PUT
        await api.put(`/language-hub/${editingItem._id}`, payload, { withCredentials: true });
        setSuccess('Item updated successfully!');
      } else {
        // POST
        await api.post('/language-hub', payload, { withCredentials: true });
        setSuccess('Item created successfully!');
      }

      setDialogOpen(false);
      fetchItems();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error saving item');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    setError(null);
    setSuccess(null);
    try {
      await api.delete(`/language-hub/${id}`, { withCredentials: true });
      setSuccess('Item deleted successfully!');
      fetchItems();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error deleting item');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Language Hub Content Manager</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openAddDialog}>
          Add New Content
        </Button>
      </Box>

      {/* Selector and Search */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Content Type</InputLabel>
            <Select
              value={selectedType}
              label="Content Type"
              onChange={(e) => { setSelectedType(e.target.value); setPage(1); }}
            >
              {sectionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <form onSubmit={handleSearch}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Search items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button type="submit" variant="outlined" sx={{ px: 4 }}>
                Search
              </Button>
            </Box>
          </form>
        </Grid>
      </Grid>

      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Items list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><Spinner /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info">No content found. Click "Add New Content" to populate the database.</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid item xs={12} key={item._id}>
                <Card sx={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2 }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item.word || item.idiom || item.title || 'Untitled Entry'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.definition || item.meaning || item.passage || item.content || ''}
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton color="primary" onClick={() => openEditDialog(item)} sx={{ mr: 1 }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(item._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination count={totalPages} page={page} onChange={(e, p) => setPage(p)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Add / Edit Dialog Modal */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Content' : 'Add New Content'} ({selectedType.toUpperCase()})</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {selectedType === 'dictionary' && (
            <>
              <TextField fullWidth label="Word" value={word} onChange={e => setWord(e.target.value)} />
              <TextField
                fullWidth
                select
                label="Part of Speech"
                value={partOfSpeech}
                onChange={e => setPartOfSpeech(e.target.value)}
              >
                <MenuItem value="">Infer from the definition</MenuItem>
                <MenuItem value="noun">Noun</MenuItem>
                <MenuItem value="verb">Verb</MenuItem>
                <MenuItem value="adjective">Adjective</MenuItem>
                <MenuItem value="adverb">Adverb</MenuItem>
                <MenuItem value="preposition">Preposition</MenuItem>
                <MenuItem value="conjunction">Conjunction</MenuItem>
                <MenuItem value="pronoun">Pronoun</MenuItem>
                <MenuItem value="interjection">Interjection</MenuItem>
                <MenuItem value="noun/verb">Noun / verb</MenuItem>
                <MenuItem value="noun/adjective">Noun / adjective</MenuItem>
                <MenuItem value="preposition/conjunction">Preposition / conjunction</MenuItem>
                <MenuItem value="determiner">Determiner</MenuItem>
                <MenuItem value="contraction">Contraction</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label="Synonyms"
                helperText="Separate synonyms with semicolons or commas."
                value={synonymsText}
                onChange={e => setSynonymsText(e.target.value)}
              />
              <TextField
                fullWidth
                label="Antonyms"
                helperText="Separate antonyms with semicolons or commas."
                value={antonymsText}
                onChange={e => setAntonymsText(e.target.value)}
              />
              <TextField fullWidth multiline rows={3} label="Definition" value={definition} onChange={e => setDefinition(e.target.value)} />
              <TextField fullWidth label="Example Sentence" value={example} onChange={e => setExample(e.target.value)} />
            </>
          )}

          {selectedType === 'idiom' && (
            <>
              <TextField fullWidth label="Idiom" value={idiom} onChange={e => setIdiom(e.target.value)} />
              <TextField fullWidth multiline rows={3} label="Meaning" value={meaning} onChange={e => setMeaning(e.target.value)} />
              <TextField fullWidth label="Example Usage" value={example} onChange={e => setExample(e.target.value)} />
            </>
          )}

          {selectedType === 'grammar' && (
            <>
              <TextField fullWidth label="Title" value={title} onChange={e => setTitle(e.target.value)} />
              <TextField fullWidth label="Category (e.g. Syntax, Punctuation)" value={category} onChange={e => setCategory(e.target.value)} />
              <TextField fullWidth multiline rows={6} label="Guide Content" value={content} onChange={e => setContent(e.target.value)} />
              <TextField fullWidth multiline rows={3} label="Practice Examples (separate with pipe | for bad/good contrast, line breaks for new items)" value={example} onChange={e => setExample(e.target.value)} />
            </>
          )}

          {selectedType === 'writing' && (
            <>
              <TextField fullWidth label="Title" value={title} onChange={e => setTitle(e.target.value)} />
              <TextField fullWidth label="Category (e.g. Essay Structures, Templates)" value={category} onChange={e => setCategory(e.target.value)} />
              <TextField fullWidth multiline rows={8} label="Template / Guide Content" value={content} onChange={e => setContent(e.target.value)} />
            </>
          )}

          {selectedType === 'reading' && (
            <>
              <TextField fullWidth label="Title" value={title} onChange={e => setTitle(e.target.value)} />
              <TextField fullWidth label="Difficulty Category (e.g. Intermediate, Advanced)" value={category} onChange={e => setCategory(e.target.value)} />
              <TextField fullWidth multiline rows={6} label="Passage Text" value={passage} onChange={e => setPassage(e.target.value)} />
              
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Comprehension Question (Single Graded Question)</Typography>
              <TextField fullWidth label="Question" value={quizQuestion} onChange={e => setQuizQuestion(e.target.value)} />
              <TextField fullWidth label="Option A" value={quizOptionA} onChange={e => setQuizOptionA(e.target.value)} />
              <TextField fullWidth label="Option B" value={quizOptionB} onChange={e => setQuizOptionB(e.target.value)} />
              <TextField fullWidth label="Option C" value={quizOptionC} onChange={e => setQuizOptionC(e.target.value)} />
              <TextField fullWidth label="Option D" value={quizOptionD} onChange={e => setQuizOptionD(e.target.value)} />
              <FormControl fullWidth>
                <InputLabel>Correct Answer Option</InputLabel>
                <Select value={quizCorrect} label="Correct Answer Option" onChange={e => setQuizCorrect(e.target.value)}>
                  <MenuItem value="A">Option A</MenuItem>
                  <MenuItem value="B">Option B</MenuItem>
                  <MenuItem value="C">Option C</MenuItem>
                  <MenuItem value="D">Option D</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth multiline rows={2} label="Answer Explanation" value={quizExplanation} onChange={e => setQuizExplanation(e.target.value)} />
            </>
          )}

          {selectedType === 'etymology' && (
            <>
              <TextField fullWidth label="Word" value={word} onChange={e => setWord(e.target.value)} />
              <TextField fullWidth label="Origin Language (e.g. Latin, Greek)" value={origin} onChange={e => setOrigin(e.target.value)} />
              <TextField fullWidth label="Definition" value={definition} onChange={e => setDefinition(e.target.value)} />
              <TextField fullWidth multiline rows={4} label="Origin History" value={history} onChange={e => setHistory(e.target.value)} />
            </>
          )}

          {selectedType === 'syllabification' && (
            <>
              <TextField fullWidth label="Word" value={word} onChange={e => setWord(e.target.value)} />
              <TextField fullWidth label="Parts (comma-separated: e.g. com, mu, ni, ca, tion)" value={parts} onChange={e => setParts(e.target.value)} />
              <TextField fullWidth type="number" label="Syllables Count (Beats)" value={beats} onChange={e => setBeats(parseInt(e.target.value) || 1)} />
              <TextField fullWidth multiline rows={2} label="Definition" value={definition} onChange={e => setDefinition(e.target.value)} />
            </>
          )}

          {selectedType === 'daily-challenge' && (
            <>
              <TextField fullWidth label="Word of the Day" value={word} onChange={e => setWord(e.target.value)} />
              <TextField fullWidth multiline rows={3} label="Definition" value={definition} onChange={e => setDefinition(e.target.value)} />
              <TextField fullWidth label="Example Sentence" value={example} onChange={e => setExample(e.target.value)} />
              <TextField fullWidth type="date" label="Release Date" InputLabelProps={{ shrink: true }} value={date} onChange={e => setDate(e.target.value)} />
              
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Mini-Challenge Question</Typography>
              <TextField fullWidth label="Question" value={quizQuestion} onChange={e => setQuizQuestion(e.target.value)} />
              <TextField fullWidth label="Option A" value={quizOptionA} onChange={e => setQuizOptionA(e.target.value)} />
              <TextField fullWidth label="Option B" value={quizOptionB} onChange={e => setQuizOptionB(e.target.value)} />
              <TextField fullWidth label="Option C" value={quizOptionC} onChange={e => setQuizOptionC(e.target.value)} />
              <TextField fullWidth label="Option D" value={quizOptionD} onChange={e => setQuizOptionD(e.target.value)} />
              <FormControl fullWidth>
                <InputLabel>Correct Answer Option</InputLabel>
                <Select value={quizCorrect} label="Correct Answer Option" onChange={e => setQuizCorrect(e.target.value)}>
                  <MenuItem value="A">Option A</MenuItem>
                  <MenuItem value="B">Option B</MenuItem>
                  <MenuItem value="C">Option C</MenuItem>
                  <MenuItem value="D">Option D</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth multiline rows={2} label="Answer Explanation" value={quizExplanation} onChange={e => setQuizExplanation(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={(!word && !idiom && !title)}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLanguageHubTab;
